import { RepliesQuery } from '__generated__/graphql/legacy/graphql';
import classNames from 'classnames/bind';
import { SkeletonCommentContainer } from 'koba/components/SkeletonComment';
import { Caption } from 'koba/components/Typography';
import { TFunction } from 'next-i18next';

import React, { RefObject, useEffect } from 'react';

import { Button } from '@thinkific/chalk/Button';

import Comment from 'components/Comment';

import styles from './CommentList.module.scss';

const cx = classNames.bind(styles);

interface CommentListProps {
  lastCommentRef?: RefObject<HTMLDivElement | null>;
  parentPostId: string;
  t: TFunction;
  onPostChange: () => void;
  data?: RepliesQuery;
  loading: boolean;
  fetchMore: (variables: unknown) => void;
  containerRef?: RefObject<HTMLDivElement | null>;
  isReadOnlySpace?: boolean;
}

export const CommentList: React.FC<CommentListProps> = ({
  parentPostId,
  t,
  lastCommentRef,
  containerRef,
  onPostChange,
  data,
  isReadOnlySpace = false,
  loading,
  fetchMore,
}) => {
  useEffect(() => {
    if (containerRef?.current && typeof containerRef.current.scrollTo === 'function') {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [containerRef]);

  const postReplies = data?.community?.post?.replies;
  const commentEdges = postReplies?.edges;
  const commentEdgesLength = commentEdges?.length || 0;
  const pageInfo = postReplies?.pageInfo;
  const totalCount = postReplies?.totalCount;

  return (
    <>
      {loading && <SkeletonCommentContainer />}
      {!loading && pageInfo?.hasPreviousPage && (
        <div className={cx('comment-list-button')} data-qa="comment-list-button">
          <Button
            className={cx('comment-list-button__previous-comments')}
            emphasis="low"
            onClick={() => {
              fetchMore({ variables: { startCursor: pageInfo.startCursor } });
            }}
          >
            {t('View previous comments')}
          </Button>
          <Caption className={cx('comment-list-button__total-count')}>
            {t('{{portion}} of {{total}}', {
              portion: commentEdgesLength,
              total: totalCount,
            })}
          </Caption>
        </div>
      )}
      {commentEdges?.map((edge, index) => {
        const node = edge?.node;
        if (node) {
          const commentRef = index === commentEdgesLength - 1 ? lastCommentRef : null;
          return (
            <div className={cx('comment-list')} key={node.id}>
              <Comment
                comment={node}
                commentRef={commentRef}
                hasReplies={node.replies.totalCount > 0}
                isFollowingThread={node.isFollowing}
                isReadOnlySpace={isReadOnlySpace}
                key={node.id}
                parentPostId={parentPostId}
                t={t}
                totalReplyCount={node.replies.totalCount}
                // @ts-ignore
                user={node.user}
                onPostChange={onPostChange}
              />
            </div>
          );
        }
        return null;
      })}
    </>
  );
};

export default CommentList;
